// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ReactNode } from 'react';

import styles from './StripCard.module.css';

export type StripCardVariant = 'type' | 'part' | 'sample' | 'room' | 'page' | 'dashed';

export interface StripCardProps {
  variant: StripCardVariant;
  /** The name row; for `dashed`, the whole label ("+ Tilføj uden geometri"). */
  title: string;
  /** A swatch before the title: a token name (`'--mat-tegl'`), or null for the ghost. */
  swatch?: string | null;
  /** The big figure ("12,4 t", "94 %", the room). */
  figure?: ReactNode;
  /** The figure is a placeholder ("tons mangler", "Mangler data"): muted and small. */
  figureMuted?: boolean;
  /** Footer, left: a code or count (mono). */
  meta?: ReactNode;
  /** Footer, right: a status pill. */
  status?: ReactNode;
  active?: boolean;
  /** Added by hand, without geometry: an inset outline. */
  manual?: boolean;
  disabled?: boolean;
  onClick: () => void;
}

/**
 * One card in the strip (v8 `.cd`): name, figure, footer. The active card
 * widens and takes the selection colour; `dashed` is the "add" card.
 */
export function StripCard({
  variant,
  title,
  swatch,
  figure,
  figureMuted,
  meta,
  status,
  active = false,
  manual = false,
  disabled = false,
  onClick,
}: StripCardProps) {
  if (variant === 'dashed') {
    return (
      <button type="button" className={`${styles.card} ${styles.dashed}`} onClick={onClick} disabled={disabled}>
        <span className={styles.dashedLabel}>{title}</span>
      </button>
    );
  }
  const cls = [styles.card, styles[variant], active ? styles.on : '', manual ? styles.manual : ''].filter(Boolean).join(' ');
  return (
    <button type="button" className={cls} aria-pressed={active} onClick={onClick} disabled={disabled}>
      <span className={styles.name}>
        {swatch !== undefined && (
          <i
            className={swatch ? styles.swatch : `${styles.swatch} ${styles.ghost}`}
            style={swatch ? { background: `var(${swatch})` } : undefined}
            aria-hidden="true"
          />
        )}
        <span className={styles.nameText}>{title}</span>
      </span>
      <span className={figureMuted ? `${styles.figure} ${styles.muted}` : styles.figure}>{figure}</span>
      <span className={styles.foot}>
        <span className={styles.meta}>{meta}</span>
        {status}
      </span>
    </button>
  );
}
